<template>
  <div class="tt-l-static-masonry" ref="wall" :style="{ 'flex-basis': columnWidth }">
    <div class="tt-l-static-masonry__column"
         v-for="(col, i) in columns" :key="i" :style="{ 'max-width': columnWidth }">
      <div class="tt-l-static-masonry__item" v-for="(item, j) in col" :key="j" :style="itemStyles">
        <slot v-bind:item="item" :index="j"></slot>
      </div>
      <div class="tt-l-static-masonry__bottom" :ref="setBottomRef"/>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * This component is named "Static" in the sense that there is no scolling.
 */
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'

export default {
  name: 'StaticMasonryLayout',
  data() {
    return {
      bottomRefs: [],
      breakpoint: null,
      columns: [],
      columnWidth: 0
    }
  },
  props: {
    breakpointsConfig: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  computed: {
    itemStyles() {
      return this.breakpoint ? { 'margin-bottom': `${this.breakpoint.vSpacing}px` } : {}
    }
  },
  methods: {
    bindResize() {
      // remove event first so we don't accidentally add it twice
      window.removeEventListener('resize', this.onResize, false)
      window.addEventListener('resize', this.onResize, false)
    },
    fill(itemIndex = 0) {
      this.$nextTick(() => {
        let colIndex = 0
        if (itemIndex < this.columns.length) {
          // no need to determine which column to place in for the first row of items
          colIndex = itemIndex
        } else {
          // determine the shortest column
          let shortestColPos = this.bottomRefs[0].getBoundingClientRect().bottom
          for (let i = 1; i < this.columns.length; i++) {
            const nextHeight = this.bottomRefs[i].getBoundingClientRect().bottom
            if (nextHeight < shortestColPos) {
              shortestColPos = nextHeight
              colIndex = i
            }
          }
        }
        this.columns[colIndex].push(this.items[itemIndex])
        const nextIndex = itemIndex + 1
        if (this.items.length > nextIndex) {
          this.fill(nextIndex)
        }
      })
    },
    onResize: debounce(function cb() {
      if (this.recalculate(true)) {
        this.fill()
      }
    }, 200),
    recalculate(fromResize = false) {
      // this method determines numberOfColumns and tileWidth
      const containerWidth = this.$parent.$el.clientWidth
      // get the highest breakpoint where viewport is closest, but equal or above the
      // container width
      const bp = this.breakpointsConfig
        .filter(item => containerWidth >= item.viewport)
        .sort((a, b) => a.viewport > b.viewport ? -1 : 1)[0]
      this.breakpoint = bp

      const numCols = bp.numOfCols
      // calculate column width as a percentage taking into account eh horizontal spacing
      const widthLessSpacing = containerWidth - (bp.hSpacing * (numCols - 1))
      this.columnWidth = Math.floor((widthLessSpacing / numCols) / containerWidth * 100) + '%'

      if (!fromResize || this.columns.length !== numCols) {
        const newCols = []
        for (let i = 0; i < numCols; i++) {
          newCols.push([])
        }
        this.columns = newCols
        return true
      }
      return false
    },
    setBottomRef(el) {
      if (el) {
        this.bottomRefs.push(el)
      }
    }
  },
  beforeUpdate() {
    this.bottomRefs = []
  },
  mounted() {
    domUtils.whenElementIsVisible(() => this.$el, 50).then(() => {
      this.recalculate()
      this.fill()
    }).catch(() => {})
    this.bindResize()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize, false)
  },
  watch: {
    items: {
      handler() {
        this.recalculate()
        this.fill()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" src="./static-masonry-layout.scss"></style>
